import { JSX, useState, ReactElement } from 'react';
import {
    Box,
    Grid,
    Checkbox,
    Alert,
    Snackbar,
    Typography,
    Tooltip,
} from '@mui/material';
import {
    InfiniteList,
    Datagrid,
    TextField,
    SelectField,
    Identifier,
    SaveButton,
    WithRecord,
    TextInput
} from 'react-admin';
import { nokTypes } from "../../../../defaults/defaults";
import { Nok } from "../../../../types/entities";
import { NokConfigureDialog } from './NokConfigureDialog';
import { useWatch, FormProvider, useForm } from "react-hook-form";
import TestEmailParser from './TestEmailParser';
import { NokTestingTestingModal } from './NokTestingTestingModal';
import {NokTestingContainerProps, FormProps} from "./types/nok-list-types";

type ActiveNokItemProps = {
    record: Nok;
}

// The form seeds testEnquiry/testResponse with a blank entry, so they are never undefined.
// Presence tells us nothing — an email counts as supplied only once it actually has content,
// whether that came from an EML upload or was typed in by hand.
const hasEmailContent: (email: any) => boolean = (email: any): boolean => {
    const entry: any = email?.[0];

    if (entry === undefined || entry === null) {
        return false;
    }

    const fields: string[] = ['subject', 'body_plain', 'body_html'];

    return fields.some( (field: string): boolean => (entry?.[field] ?? '').trim().length > 0 );
}

// Kept as its own component so that watching the form does not re-render the whole testing
// form — including both email editors — on every keystroke.
const RunTestButton: () => ReactElement = (): ReactElement => {
    const noks: Nok[] = useWatch({ name: 'noks' }) ?? [];
    const testEnquiry: any = useWatch({ name: 'testEnquiry' });
    const testResponse: any = useWatch({ name: 'testResponse' });

    const missing: string[] = [];

    if (noks.length === 0) {
        missing.push('select at least one Nok');
    }
    if (!hasEmailContent(testEnquiry)) {
        missing.push('add a test enquiry email');
    }
    if (!hasEmailContent(testResponse)) {
        missing.push('add a test response email');
    }

    const isDisabled: boolean = missing.length > 0;

    return (
        // The span is required: a disabled button fires no events, so Tooltip needs a wrapper
        // that can still receive hover.
        <Tooltip title={ isDisabled ? `Before running a test: ${missing.join(', ')}.` : '' }>
            <span>
                <SaveButton label="Run Test"
                            color="success"
                            icon={ false }
                            disabled={ isDisabled }
                            sx={ { width: '150px', height: '40px' } }
                />
            </span>
        </Tooltip>
    );
}

const ActiveNokItem: (props: ActiveNokItemProps) => ReactElement = (props: ActiveNokItemProps): ReactElement => {
    const record: Nok = props?.record;
    // useWatch subscribes to the field, so the checkbox repaints as soon as the Nok is
    // added or removed. getValues() does not subscribe — it only appeared to work because
    // closing the configure dialog happened to re-render this list.
    const activeNoks: Nok[] = useWatch({ name: 'noks' }) ?? [];

    const isNokActive: boolean = activeNoks.some(
        (nok: Nok): boolean => nok?.operatorId === record?.operatorId
    );

    return (
        <Checkbox checked={ isNokActive } readOnly />
    );
}

export const NokTestingContainer: (props: NokTestingContainerProps) => JSX.Element = (props: NokTestingContainerProps): JSX.Element => {
    const [activeConfigureNok, setActiveConfigureNok] = useState<Nok|undefined>();
    const [showConfigureModal, setShowConfigureModal] = useState<boolean>(false);
    const [nokTestingData, setNokTestingData] = useState<any|undefined>();
    const [outboundFileName, setOutboundFileName] = useState<string>('');
    const [inboundFileName, setInboundFileName] = useState<string>('');
    const [error, setError] = useState<string|undefined>();
    // Unannotated per the HQ typing convention: useForm's own generic carries the type, and
    // UseFormReturn's parameter list has changed across react-hook-form versions.
    const form = useForm<FormProps>({
        defaultValues: {
            noks: [],
            // body_plain, not body: the EML parser writes body_plain and nothing ever read
            // body, so the seeded key never matched the real shape.
            testEnquiry: [
                {
                    subject: '',
                    body_plain: '',
                    signature: '',
                    headers: []
                }
            ],
            testResponse: [
                {
                    subject: '',
                    body_plain: '',
                    signature: '',
                    headers: []
                }
            ],
            target_company: ''
        }
    });
    async function handleFormSubmit(data: FormProps): Promise<void> {
        props?.handleButtonDisable(true);
        if (data?.noks?.length === 0) {
            setError('At least one NOK must be selected for testing');
            props?.handleButtonDisable(false);
            return;
        }
        const hasEnquiry: boolean = hasEmailContent(data?.testEnquiry);
        const hasResponse: boolean = hasEmailContent(data?.testResponse);

        if (!hasEnquiry || !hasResponse) {
            let missing: string = 'a test enquiry and a test response email';
            if (hasEnquiry) {
                missing = 'a test response email';
            }
            if (hasResponse) {
                missing = 'a test enquiry email';
            }
            setError(`Please add ${missing} before running a test`);
            props?.handleButtonDisable(false);
            return;
        }
        let datum: FormProps & { isTest?: boolean, domain?: string } = data;
        datum.domain = data.testResponse[0].domain;
        datum.isTest = true
        setNokTestingData(datum);
        // Deliberate, not a stray debug line: this logs the exact payload sent to the Operator
        // and is used as a reference while testing. Leave it in place.
        console.log(datum);
        props?.handleButtonDisable(false);

    }

    function handleToastCancel(): void {
        setError(undefined);
    }

    function handleConfigureNok(nok: Nok): void {
        setActiveConfigureNok(nok);
        setShowConfigureModal(true);
    }

    function handleCloseConfigureModal(): void {
        setShowConfigureModal(false);
        setActiveConfigureNok(undefined);
    }

    function resetNokTestingData(): void {
        setNokTestingData(undefined);
    }

    function handleOutboundFileName(fileName: string): void {
        setOutboundFileName(fileName);
    }

    function handleInboundFileName(fileName: string): void {
        setInboundFileName(fileName);
    }

    return (
        <>
            <FormProvider { ...form }>
                <form onSubmit={ form.handleSubmit(handleFormSubmit) }
                      style={ {
                          width: '100%',
                          height: '100%',
                      } }
                >

                    <Grid container spacing={ 2 } sx={ { width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: '15px', padding: '10px', margin: '20px 0 0 0', maxWidth: '100%' } } >
                        <Grid size={ 12 }>
                            <Box sx={ {
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 2,
                            } }>
                                { /* Deliberately not required(). target_company and the domain
                                     taken from the response email matter for some tests but not
                                     all, so neither is enforced and neither blocks Run Test. */ }
                                <TextInput source="target_company"
                                           helperText="Company Enquiry was sent to"
                                           sx={ { maxWidth: '400px', flex: '1 1 240px' } }
                                />
                                <RunTestButton />
                            </Box>
                        </Grid>
                        <Grid size={ { xs: 12, md: 4 } }>

                            <Box sx={ {
                                display: 'flex',
                                flexDirection: 'column',
                                margin: '10px 0 0 0'
                            } }>
                                <Typography variant="subtitle2" component="h2" sx={ { mb: 1 } }>{ "Select Noks to test" }</Typography>
                                <InfiniteList disableSyncWithLocation
                                              perPage={ 50 }
                                              filter={ { status: 'active' } }
                                              sx={ {
                                                  '& .RaList-noActions': {
                                                      marginTop: 0
                                                  }
                                              } }
                                              actions={ false }>
                                    <Datagrid bulkActionButtons={ false }
                                              rowClick={ (id: Identifier, resource: string, record: Nok): false => {
                                                  handleConfigureNok(record);
                                                  return false;
                                              } }
                                    >
                                        <TextField source="name" label="Nok" />
                                        <SelectField source="type" choices={ nokTypes } />
                                        <WithRecord label="Selected" render={
                                            (record: Nok): ReactElement => {

                                                return (
                                                    <ActiveNokItem record={ record } />
                                                );
                                            }
                                        } />
                                    </Datagrid>
                                </InfiniteList>
                            </Box>

                            <NokConfigureDialog open={ showConfigureModal }
                                                handleClose={ handleCloseConfigureModal}
                                                activeConfigureNok={ activeConfigureNok }
                            />

                        </Grid>
                        <Grid size={ { xs: 12, md: 4 } }>
                            <Typography variant="subtitle2" component="h2">{ "Test Enquiry Email" }</Typography>
                            <Typography variant="caption" color="text.secondary">{ "Upload an EML file or enter the details manually" }</Typography>
                            <TestEmailParser source="testEnquiry" onFileLoaded={ handleOutboundFileName } />
                        </Grid>
                        <Grid size={ { xs: 12, md: 4 } }>
                            <Typography variant="subtitle2" component="h2">{ "Test Response Email" }</Typography>
                            <Typography variant="caption" color="text.secondary">{ "Upload an EML file or enter the details manually" }</Typography>
                            <TestEmailParser source="testResponse" onFileLoaded={ handleInboundFileName } />
                        </Grid>

                    </Grid>
                </form>
                <Snackbar open={ error !== undefined }
                          autoHideDuration={ 6000 }
                          onClose={ handleToastCancel }
                          anchorOrigin={ { vertical: 'top', horizontal: 'center' } }
                          // Clear the React Admin AppBar, which the toast would otherwise
                          // open behind. RA forces a 48px toolbar below the md breakpoint
                          // and inherits MUI's 64px at md and above, so the offset follows
                          // the same boundary.
                          sx={ { top: { xs: 56, md: 72 } } }
                >
                    <Alert severity="error" variant="filled" onClose={ handleToastCancel }>
                        { error }
                    </Alert>
                </Snackbar>
            </FormProvider>
            <NokTestingTestingModal  nokTestingData={ nokTestingData }
                                     resetNokTestingData={ resetNokTestingData }
                                     outboundFileName={ outboundFileName }
                                     inboundFileName={ inboundFileName }
                                     form={ form }
            />
        </>
    );
}